import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import IconSvg, { Icons } from 'src/assets/icons/IconSvg';
import RaffleFilterMenu from 'src/components/dropdowns/RaffleFilterMenu';
import SortByMenu from 'src/components/dropdowns/SortByMenu';
import SearchRaffleInput from './components/SearchRaffleInput';
import CreatedRaffleTab from './tabs/CreatedRaffleTab';
import FavoriteRaffleTab from './tabs/FavoriteRaffleTab';
import JoinedRafflesTab from './tabs/JoinedRafflesTab';
import { useViewport } from 'src/context/ViewportContext';
import { useHttp, useWallet } from 'src/hooks';
import { useUserRaffles } from 'src/hooks/useUserRaffles';
import { useNotification } from 'src/hooks/useNotification';
import { useWalletBalance } from 'src/hooks/useWalletBalance';
import { useRaffleControls } from 'src/hooks/useRaffleControls';
import { appConfig } from 'src/config/appConfig';
import { AuthContext } from 'src/contexts';
import SyncLoader from 'react-spinners/SyncLoader';

const tabKeys = ['createdRaffles', 'joinedRaffles', 'favoriteRaffles'] as const;

const tabs: { label: string; icon: Icons; key: string }[] = [
  { label: 'Created Raffles', icon: 'createS', key: 'createdRaffles' },
  { label: 'Joined Raffles', icon: 'usersS', key: 'joinedRaffles' },
  { label: 'Favorite Raffles', icon: 'transactionS', key: 'favoriteRaffles' },
];

const Profile = () => {
  const [searchParams] = useSearchParams();
  const { sendRequest: fetchProfile, isLoading: isLoadingProfile } = useHttp();
  const { sendRequest } = useHttp();
  const { accessToken } = useContext(AuthContext);

  const initialTab = tabKeys.includes(searchParams.get('tab') as any)
    ? (searchParams.get('tab') as string)
    : 'createdRaffles';

  const [activeView, setActiveView] = useState<'grid' | 'list'>('grid');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [userId, setUserId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const { isMobile } = useViewport();
  const { isConnected, providerId, paymentAddress, ordinalsAddress, displayAddress } = useWallet();
  const notification = useNotification();
  const walletAddress = ordinalsAddress?.address || paymentAddress?.address || '';
  const { balance, isLoading: balanceLoading } = useWalletBalance(paymentAddress?.address);

  // Ensure user exists in backend
  useEffect(() => {
    if (!isConnected || !providerId || !walletAddress) {
      setUserId(null);
      return;
    }

    axios
      .post(`${appConfig.serviceUrl}/users/ensure`, {
        walletId: providerId,
        walletAddress,
      })
      .then((res) => setUserId(res.data.id))
      .catch(() => setUserId(null));
  }, [isConnected, providerId, walletAddress]);

  // Fetch updated user profile
  useEffect(() => {
    if (!accessToken) {
      setUserProfile(null);
      return;
    }

    fetchProfile(
      {
        url: 'users/me',
        method: 'GET',
        accessToken,
      },
      (data: any) => {
        setUserProfile(data);
      }
    );
  }, [accessToken, fetchProfile]);

  const {
    createdRaffles,
    joinedRaffles,
    favoriteRaffles,
    stats,
    loadingCreated,
    loadingJoined,
    loadingFavorites,
    fetchCreated,
    fetchJoined,
    fetchFavorites,
    fetchStats,
  } = useUserRaffles(userId);

  const {
    controls,
    setSearchQuery,
    setSort,
    setFilters,
    resetControls,
    processRaffles,
    processFavorites,
  } = useRaffleControls();

  const processedCreated = useMemo(
    () => processRaffles(createdRaffles),
    [createdRaffles, processRaffles],
  );
  const processedJoined = useMemo(
    () => processRaffles(joinedRaffles),
    [joinedRaffles, processRaffles],
  );
  const processedFavorites = useMemo(
    () => processFavorites(favoriteRaffles),
    [favoriteRaffles, processFavorites],
  );

  // Set of favorited raffle IDs for quick lookup
  const favoriteRaffleIds = useMemo(
    () => new Set(favoriteRaffles.map((f) => f.raffleId)),
    [favoriteRaffles],
  );

  const handleToggleFavorite = useCallback(
    async (raffleId: string) => {
      if (!userId) return;
      try {
        await axios.post(`${appConfig.serviceUrl}/raffles/${raffleId}/favorite`, { userId });
        fetchFavorites();
      } catch {
        notification('Failed to update favorite', { type: 'error' });
      }
    },
    [userId, fetchFavorites, notification],
  );

  const handleClaimPrize = useCallback(
    async (raffleId: string) => {
      if (!userId) return;
      try {
        await axios.patch(`${appConfig.serviceUrl}/raffles/${raffleId}/claim`, { userId });
        notification('Prize claimed successfully!', { type: 'success' });
        fetchJoined();
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to claim prize';
        notification(msg, { type: 'error' });
      }
    },
    [userId, fetchJoined, notification],
  );

  const handleCancelled = useCallback(() => {
    fetchCreated();
    fetchStats();
  }, [fetchCreated, fetchStats]);

  const handleViewClick = (view: 'grid' | 'list') => {
    setActiveView(view);
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    resetControls();
  };

  const views: { key: 'grid' | 'list'; icon: Icons }[] = [
    { key: 'grid', icon: isMobile ? 'gridS' : 'gridM' },
    { key: 'list', icon: isMobile ? 'listS' : 'listM' },
  ];

  const sortedTabs = isMobile ? [...tabs].sort((a) => (a.key === activeTab ? -1 : 1)) : tabs;

  const truncatedAddress = displayAddress
    ? `${displayAddress.slice(0, 6)}...${displayAddress.slice(-4)}`
    : 'Not connected';
  const handleConnectX = () => {

    // if (address) {
    sendRequest(
      {
        url: `twitter/login`,
        method: 'GET',
        accessToken

      },
      (data: any) => {
        if (data) {
          // window.open(data, '_blank');

          window.location.href = data;
        }
      },
    );

  };
  const handleJoinTelegram = () => {
    sendRequest(
      {
        url: `telegram/login`,
        method: 'GET',
        accessToken,
      },
      (data: any) => {
        if (data?.url) {
          // window.open(data.url, '_blank');
          window.location.href = data.url;
        }
      },
    );

  };

  const handleConnectDiscord = () => {
    sendRequest(
      {
        url: `discord/login`,
        method: 'GET',
        accessToken,
      },
      (data: any) => {
        if (data?.url) {
          window.location.href = data.url;
        } else if (typeof data === 'string') {
          // Fallback in case the API directly returns the URL string
          window.location.href = data;
        }
      },
    );
  };

  const handleDisconnectSocial = (provider: 'discord' | 'twitter' | 'telegram') => {
    sendRequest(
      {
        url: `users/me/social/${provider}`,
        method: 'DELETE',
        accessToken,
      },
      () => {
        // Re-fetch profile to update UI
        fetchProfile(
          {
            url: 'users/me',
            method: 'GET',
            accessToken,
          },
          (data: any) => {
            setUserProfile(data);
          },
        );
      },
    );
  };

  return (
    <>
      <div className="flex flex-col flex-wrap gap-4 mb-6 2xl:items-center 2xl:justify-between 2xl:mb-8 2xl:flex-row">
        <div className="flex flex-col gap-4 2xl:items-center 2xl:flex-row">
          <div className="flex gap-3 items-center">
            <div className="w-12 h-12 2xl:w-[140px] 2xl:h-[140px] bg-white rounded-full"></div>
            <h4 className="text-brand-magnolia 2xl:hidden">{truncatedAddress}</h4>
          </div>

          <div>
            <h2 className="text-brand-magnolia leading-[58px] 2xl:block hidden mb-3">
              {truncatedAddress}
            </h2>

            <div className="flex gap-2 items-center">
              {userProfile?.discord?.username ? (
                <Menu>
                  <MenuButton
                    disabled={isLoadingProfile}
                    className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                  >
                    <span className="text-brand-tangerine">@{userProfile.discord.username}</span>
                    <IconSvg icon="discordS" />
                  </MenuButton>
                  <MenuItems transition anchor="bottom start" className="z-50 mt-1.5 w-[160px] rounded-lg bg-grayscale-900 p-2">
                    <MenuItem>
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-system-error rounded-md data-[focus]:bg-grayscale-700"
                        onClick={() => handleDisconnectSocial('discord')}
                      >
                        Remove
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              ) : (
                <button
                  onClick={handleConnectDiscord}
                  disabled={isLoadingProfile}
                  className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                >
                  {isLoadingProfile ? <SyncLoader color="#fff" size={4} /> : <span>Connect</span>}
                  <IconSvg icon="discordS" />
                </button>
              )}

              {userProfile?.twitter?.username ? (
                <Menu>
                  <MenuButton
                    disabled={isLoadingProfile}
                    className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                  >
                    <span className="text-brand-tangerine">@{userProfile.twitter.username}</span>
                    <IconSvg icon="twitterS" />
                  </MenuButton>
                  <MenuItems transition anchor="bottom start" className="z-50 mt-1.5 w-[160px] rounded-lg bg-grayscale-900 p-2">
                    <MenuItem>
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-system-error rounded-md data-[focus]:bg-grayscale-700"
                        onClick={() => handleDisconnectSocial('twitter')}
                      >
                        Remove
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              ) : (
                <button
                  onClick={handleConnectX}
                  disabled={isLoadingProfile}
                  className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                >
                  {isLoadingProfile ? <SyncLoader color="#fff" size={4} /> : <span>Connect</span>}
                  <IconSvg icon="twitterS" />
                </button>
              )}

              {userProfile?.telegram?.username ? (
                <Menu>
                  <MenuButton
                    disabled={isLoadingProfile}
                    className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                  >
                    <span className="text-brand-tangerine">@{userProfile.telegram.username}</span>
                    <IconSvg icon="telegramS" />
                  </MenuButton>
                  <MenuItems transition anchor="bottom start" className="z-50 mt-1.5 w-[160px] rounded-lg bg-grayscale-900 p-2">
                    <MenuItem>
                      <button
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-system-error rounded-md data-[focus]:bg-grayscale-700"
                        onClick={() => handleDisconnectSocial('telegram')}
                      >
                        Remove
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              ) : (
                <button
                  onClick={handleJoinTelegram}
                  disabled={isLoadingProfile}
                  className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600 disabled:opacity-50"
                >
                  {isLoadingProfile ? <SyncLoader color="#fff" size={4} /> : <span>Connect</span>}
                  <IconSvg icon="telegramS" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex overflow-x-auto gap-2 2xl:gap-3 no-scrollbar">
          <div className="2xl:w-[172px] rounded-lg px-4 2xl:px-5 py-2 2xl:py-4 bg-grayscale-800 space-y-1 flex-shrink-0">
            <div className="text-xs 2xl:text-sm text-grayscale-400">Wallet Balance</div>
            <div className="flex gap-2 items-center">
              <IconSvg icon="bitcoinM" />
              <span className="text-brand-magnolia font-medium text-lg 2xl:text-[24px] tracking-[-0.1px]">
                {balanceLoading ? '...' : balance != null ? balance.toFixed(8).replace(/0+$/, '').replace(/\.$/, '') : '--'}
              </span>
            </div>
          </div>

          <div className="2xl:w-[172px] rounded-lg px-4 2xl:px-5 py-2 2xl:py-4 bg-grayscale-800 space-y-1 flex-shrink-0">
            <div className="text-xs 2xl:text-sm text-grayscale-400">Raffles Created</div>
            <span className="text-brand-magnolia font-medium text-lg 2xl:text-[24px] tracking-[-0.1px]">
              {stats?.rafflesCreated ?? 0}
            </span>
          </div>

          <div className="2xl:w-[172px] rounded-lg px-4 2xl:px-5 py-2 2xl:py-4 bg-grayscale-800 space-y-1 flex-shrink-0">
            <div className="text-xs 2xl:text-sm text-grayscale-400">Raffles Won</div>
            <span className="text-brand-magnolia font-medium text-lg 2xl:text-[24px] tracking-[-0.1px]">
              {stats?.rafflesWon ?? 0}
            </span>
          </div>

          <div className="2xl:w-[172px] rounded-lg px-4 2xl:px-5 py-2 2xl:py-4 bg-grayscale-800 space-y-1 flex-shrink-0">
            <div className="text-xs 2xl:text-sm text-grayscale-400">Tickets Sold</div>
            <span className="text-brand-magnolia font-medium text-lg 2xl:text-[24px] tracking-[-0.1px]">
              {stats?.ticketsSold ?? 0}
            </span>
          </div>

          <div className="2xl:w-[172px] rounded-lg px-4 2xl:px-5 py-2 2xl:py-4 bg-grayscale-800 space-y-1 flex-shrink-0">
            <div className="text-xs 2xl:text-sm text-grayscale-400">Tickets Bought</div>
            <span className="text-brand-magnolia font-medium text-lg 2xl:text-[24px] tracking-[-0.1px]">
              {stats?.ticketsBought ?? 0}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 mb-3 2xl:mb-2 2xl:justify-between 2xl:items-center 2xl:flex-row">
        <div className="flex overflow-x-auto gap-3 items-center no-scrollbar">
          {sortedTabs.map((tab) => (
            <button
              key={tab.key}
              className={`flex gap-2 items-center py-2 px-3 rounded-lg font-medium flex-shrink-0 ${activeTab === tab.key ? 'bg-grayscale-800 text-grayscale-100' : 'text-grayscale-300'
                }`}
              onClick={() => handleTabClick(tab.key)}
            >
              <IconSvg icon={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="block w-full 2xl:hidden">
          <SearchRaffleInput value={controls.searchQuery} onChange={setSearchQuery} className="!w-full" />
        </div>

        <div className="flex gap-3 justify-between items-center w-full 2xl:justify-end 2xl:w-fit">
          <div className="flex rounded-md bg-grayscale-900 w-fit">
            {views.map((view) => (
              <button
                key={view.key}
                className={`p-2.5 flex items-center justify-center transition-all duration-200 w-fit ${activeView === view.key
                  ? 'bg-grayscale-800 text-brand-tangerine rounded-md'
                  : 'text-grayscale-500'
                  }`}
                onClick={() => handleViewClick(view.key)}
              >
                <IconSvg icon={view.icon as Icons} />
              </button>
            ))}
          </div>

          <div className="hidden 2xl:block">
            <SearchRaffleInput value={controls.searchQuery} onChange={setSearchQuery} />
          </div>

          <div className="flex gap-2 items-center 2xl:gap-3">
            <SortByMenu selectedSort={controls.sort} onSortChange={setSort} />
            <RaffleFilterMenu filterState={controls.filters} onApplyFilters={setFilters} />
          </div>
        </div>
      </div>

      {!isConnected && (
        <div className="py-10 text-center text-grayscale-400">
          Connect your wallet to view your profile
        </div>
      )}

      {isConnected && activeTab === 'createdRaffles' && (
        <CreatedRaffleTab
          activeView={activeView}
          raffles={processedCreated}
          loading={loadingCreated}
          userId={userId ?? ''}
          onCancelled={handleCancelled}
        />
      )}
      {isConnected && activeTab === 'joinedRaffles' && (
        <JoinedRafflesTab
          activeView={activeView}
          raffles={processedJoined}
          loading={loadingJoined}
          userId={userId ?? ''}
          favoriteRaffleIds={favoriteRaffleIds}
          onToggleFavorite={handleToggleFavorite}
          onClaimPrize={handleClaimPrize}
        />
      )}
      {isConnected && activeTab === 'favoriteRaffles' && (
        <FavoriteRaffleTab
          activeView={activeView}
          favorites={processedFavorites}
          loading={loadingFavorites}
          userId={userId ?? ''}
          onToggleFavorite={handleToggleFavorite}
        />
      )}
    </>
  );
};

export default Profile;
